// Секцията „Последна инвентаризация по склад" на началния екран на
// „Инвентаризация" (по един ред на склад: кога, кой, разликите в стойност и
// „Започни" за този склад); незавършената (моята и на колегите) е общата
// `components/drafts/DraftHomeSection`. Глупав рендер — данните идват от
// страницата.
//
// Редовете са плътни (ЕДИН ред, вторичното приглушено след името). Склад с
// незавършено броене носи червено хапче „Незавършена · Мария · 12 преброени" и
// „Продължи" (моето) / „Поеми" (на колега) вместо „Започни".
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { Stocktake } from "../../../../../api/inventory";
import type { DraftSummary } from "../../../components/drafts/draftModel";
import { SECONDARY_BUTTON_CLASS } from "../../../components/list/buttonStyles";
import { ROW_BUTTON, ROW_DENSE } from "../../../components/list/density";
import { SectionCard, SectionTitle, SignalPill } from "../../../components/list/ListBits";
import { TruncatedText } from "../../../components/list/TruncatedText";
import { fmtDate } from "../../../components/dateTime";
import { formatSignedMoney, signedToneClass } from "../stocktakeMeta";
import { unfinishedStocktakeLine } from "./stocktakeUnfinished";

// Една незавършена на склада и дали е моята
export type WarehouseUnfinished = { draft: DraftSummary; mine: boolean };

// Един ред „склад": последната му инвентаризация (ако BE я знае) + документът
// ѝ (за разликите в стойност), когато е зареден, и незавършените броения
export type WarehouseStocktakeRow = {
  warehouseId: number;
  warehouseName: string;
  latest: {
    stocktakeId: number;
    date: string;
    createdByName: string | null;
    document: Stocktake | null;
  } | null;
  unfinished: WarehouseUnfinished[];
};

// „днес" / „вчера" / „преди 12 дни" — по календарен ден
const useAgeLabel = () => {
  const { t } = useTranslation();
  return (iso: string) => {
    const days = dayjs().startOf("day").diff(dayjs(iso).startOf("day"), "day");
    if (days <= 0) return t("inventoryPage.stocktakeAgoToday");
    return t("inventoryPage.stocktakeAgoDays", { count: days });
  };
};

// Бутоните в плътния ред — малките 24px (`ROW_BUTTON`), с пръст 36px
const ROW_BUTTON_CLASS = `${SECONDARY_BUTTON_CLASS} ${ROW_BUTTON}`;

export const LatestByWarehouseSection = ({
  rows,
  warehousesLoaded,
  latestKnown,
  onStart,
  onView,
}: {
  rows: WarehouseStocktakeRow[];
  // „Няма склад" се казва чак след като справочникът е зареден
  warehousesLoaded: boolean;
  // BE върна последните (стар BE / локален режим → не знаем, не казваме „няма")
  latestKnown: boolean;
  onStart: (warehouseId: number) => void;
  onView: (stocktakeId: number) => void;
}) => {
  const { t } = useTranslation();
  const ageOf = useAgeLabel();
  return (
    <section className="flex flex-col gap-3">
      <SectionTitle
        title={t("inventoryPage.stocktakeLatestTitle")}
        hint={t("inventoryPage.stocktakeLatestHint")}
      />
      <SectionCard>
        {rows.map((row, index) => {
          const latest = row.latest;
          const latestDoc = latest?.document ?? null;
          const meta = latest
            ? [
                `${fmtDate(latest.date)} · ${ageOf(latest.date)}`,
                latest.createdByName,
                t("inventoryPage.docStocktakeSubtitle", { id: latest.stocktakeId }),
              ]
                .filter(Boolean)
                .join(" · ")
            : latestKnown
              ? t("inventoryPage.stocktakeLatestNever")
              : "—";
          const first = row.unfinished[0] ?? null;
          const unfinishedText = first
            ? [
                t("inventoryPage.stkUnfinished"),
                unfinishedStocktakeLine(t, first.draft, { mine: first.mine, withWarehouse: false }),
                row.unfinished.length > 1 ? `+${row.unfinished.length - 1}` : null,
              ]
                .filter(Boolean)
                .join(" · ")
            : null;
          return (
            <div
              key={row.warehouseId}
              data-warehouse-row={row.warehouseId}
              className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-3.5 ${ROW_DENSE} ${
                index ? "border-t border-shade-1" : ""
              }`}
            >
              {/* Складът и последната инвентаризация — клик отваря документа */}
              <button
                type="button"
                disabled={!latest}
                onClick={() => latest && onView(latest.stocktakeId)}
                className="flex min-w-[200px] flex-1 cursor-pointer items-center rounded-lg border-none bg-transparent p-0 text-left text-ink outline-none focus-visible:shadow-[0_0_0_3px_var(--color-brand-tint)] disabled:cursor-default"
              >
                <TruncatedText text={`${row.warehouseName} · ${meta}`}>
                  <span className="font-semibold">{row.warehouseName}</span>
                  <span className="text-[12.5px] text-body"> · {meta}</span>
                </TruncatedText>
              </button>
              {unfinishedText && (
                <span title={unfinishedText} className="min-w-0 max-w-[320px]">
                  <SignalPill tone="critical">{unfinishedText}</SignalPill>
                </span>
              )}
              {/* Разликите на последната: брой излишъци/липси и стойността */}
              {latestDoc && (
                <span className="flex items-baseline gap-3 whitespace-nowrap">
                  <span className="font-sheet text-[13px]">
                    <span className={latestDoc.surplusCount ? "text-ok-deep" : "text-ghost"}>
                      +{latestDoc.surplusCount}
                    </span>
                    <span className="text-faint"> / </span>
                    <span className={latestDoc.shortageCount ? "text-danger-ink" : "text-ghost"}>
                      -{latestDoc.shortageCount}
                    </span>
                  </span>
                  <span
                    className={`font-sheet font-semibold ${signedToneClass(latestDoc.totalValueWithVat)}`}
                  >
                    {formatSignedMoney(latestDoc.totalValueWithVat)}
                  </span>
                </span>
              )}
              {first ? (
                <button type="button" onClick={first.draft.onOpen} className={ROW_BUTTON_CLASS}>
                  {first.mine ? t("inventoryPage.stkRowContinue") : t("inventoryPage.stkRowTakeOver")}
                </button>
              ) : (
                <button type="button" onClick={() => onStart(row.warehouseId)} className={ROW_BUTTON_CLASS}>
                  {t("inventoryPage.stocktakeStart")}
                </button>
              )}
            </div>
          );
        })}
        {rows.length === 0 && warehousesLoaded && (
          <div className="px-3.5 py-3 text-muted">{t("inventoryPage.errNoWarehouses")}</div>
        )}
      </SectionCard>
    </section>
  );
};
